import { useMemo } from 'react';
import {
  ButtonVariants,
  Dropdown,
  Field,
  FieldTypes,
  Form,
  FormSizes,
  Modal,
  ModalSizes,
  TextInput,
  Tooltip,
  Typography,
  TypographyColors,
  TypographyElements,
  TypographyVariants,
  TypographyWeights,
  useFormState,
} from '@carboledger/ui';
import { Info } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { hasRequiredUserFields, userValidation } from '../adminValidation';
import { USER_FIELDS } from '../constants/admin.constants';
import { rolePicksManySites, rolePicksOneSite } from '../constants/userRoles';
import styles from './UserModal.module.scss';

/**
 * Props:
 *   text  the field label
 *   info  the copy the trailing ⓘ tooltip shows
 */
const InfoLabel = ({ text, info }) => (
  <span className={styles.infoLabel}>
    {text}
    <Tooltip content={info}>
      <Info size={14} className={styles.infoIcon} aria-hidden="true" />
    </Tooltip>
  </span>
);

/**
 * Props:
 *   isEdit          the email is the user's identity, so Edit shows it read-only
 *   saving          disables every field while a save is in flight
 *   roleOptions     Dropdown options for User role
 *   siteOptions     Dropdown options for Sites
 *   storageOptions  Dropdown options for Storage units
 */
const UserFields = ({ isEdit, saving, roleOptions, siteOptions, storageOptions }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const role = values[USER_FIELDS.role];
  const picksManySites = rolePicksManySites(role);
  const picksOneSite = rolePicksOneSite(role);

  const sitesLabel = useMemo(
    () => <InfoLabel text={t('FIELD_SITES')} info={t('ADMIN_SITES_INFO')} />,
    [t],
  );
  const storageLabel = useMemo(
    () => (
      <InfoLabel text={t('FORM_STORAGE_UNITS_OPTIONAL')} info={t('ADMIN_STORAGE_UNITS_INFO')} />
    ),
    [t],
  );

  return (
    <div className={styles.fields}>
      <div className={styles.sectionTitle}>
        <Typography
          as={TypographyElements.span}
          variant={TypographyVariants.caption}
          weight={TypographyWeights.semibold}
          color={TypographyColors.placeholder}
          className={styles.sectionLabel}
        >
          {t('SECTION_USER_DETAILS')}
        </Typography>
        <span className={styles.sectionRule} aria-hidden="true" />
      </div>

      <Field name={USER_FIELDS.email} required rules={userValidation[USER_FIELDS.email]}>
        {({ id, name, value, onChange, onBlur, status, message }) => (
          <TextInput
            id={id}
            name={name}
            label={t('FIELD_USER_NAME')}
            placeholder={t('FORM_USER_NAME_PLACEHOLDER')}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            status={status}
            message={message}
            required
            disabled={isEdit || saving}
          />
        )}
      </Field>

      <Field name={USER_FIELDS.role} required>
        {({ id, name, value, onChange, onBlur, status, message }) => (
          <Dropdown
            id={id}
            name={name}
            label={t('FIELD_USER_ROLE')}
            placeholder={t('FORM_SELECT_USER_ROLE')}
            options={roleOptions}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            status={status}
            message={message}
            required
            disabled={saving}
          />
        )}
      </Field>

      {picksManySites && (
        <Field name={USER_FIELDS.siteIds} type={FieldTypes.multi} required>
          {({ id, name, value, onChange, status, message }) => (
            <Dropdown
              multiple
              id={id}
              name={name}
              label={sitesLabel}
              placeholder={t('FORM_SELECT_SITES')}
              options={siteOptions}
              value={value}
              onChange={onChange}
              status={status}
              message={message}
              required
              disabled={saving}
            />
          )}
        </Field>
      )}

      {picksOneSite && (
        <Field name={USER_FIELDS.siteId} required>
          {({ id, name, value, onChange, onBlur, status, message }) => (
            <Dropdown
              id={id}
              name={name}
              label={sitesLabel}
              placeholder={t('FORM_SELECT_SITES')}
              options={siteOptions}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              required
              disabled={saving}
            />
          )}
        </Field>
      )}

      {(picksManySites || picksOneSite) && (
        <Field name={USER_FIELDS.storageIds} type={FieldTypes.multi}>
          {({ id, name, value, onChange, status, message }) => (
            <Dropdown
              multiple
              id={id}
              name={name}
              label={storageLabel}
              placeholder={t('FORM_SELECT_STORAGE_UNITS')}
              options={storageOptions}
              value={value}
              onChange={onChange}
              status={status}
              message={message}
              disabled={saving}
            />
          )}
        </Field>
      )}
    </div>
  );
};

/**
 * Props:
 *   open            whether the modal is shown
 *   isEdit          Edit user (PUT) rather than Add user (invite)
 *   initialValues   the form's seed — EMPTY_USER_FORM, a list row's values, or a draft
 *   roleOptions     the roles the signed-in user may grant
 *   siteOptions     the sites the signed-in user can assign
 *   storageOptions  the storage units the signed-in user can assign
 *   saving          a save is in flight
 *   onSubmit        (values) => void — validated form values
 *   onClose         ✕ / Cancel
 */
export const UserModal = ({
  open,
  isEdit,
  initialValues,
  roleOptions,
  siteOptions,
  storageOptions,
  saving,
  onSubmit,
  onClose,
}) => {
  const t = useTranslations();

  if (!open) return null;

  return (
    <Form
      size={FormSizes.medium}
      initialValues={initialValues}
      onSubmit={onSubmit}
      className={styles.form}
    >
      {({ values, submit }) => (
        <Modal
          open
          size={ModalSizes.medium}
          title={isEdit ? t('ADMIN_EDIT_USER') : t('ADMIN_ADD_USER')}
          onClose={onClose}
          buttons={[
            { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
            {
              label: t('COMMON_SAVE'),
              variant: ButtonVariants.primary,
              disabled: saving || !hasRequiredUserFields(values),
              loading: saving,
              onClick: submit,
            },
          ]}
        >
          <UserFields
            isEdit={isEdit}
            saving={saving}
            roleOptions={roleOptions}
            siteOptions={siteOptions}
            storageOptions={storageOptions}
          />
        </Modal>
      )}
    </Form>
  );
};
